/**
 * The transcript (WS6). System-sans agent prose with a thin accent left
 * rule, no bubbles, no avatars; compact user turns with a "You" cap inset
 * (DESIGN §2.1). Renders replayed display_json (server truth) plus the live
 * stream buffer for the in-flight turn.
 *
 * Frames are RAW on the persisted side (DECISIONS #62): retired canvas/
 * knowledge frame types ride through old sessions and the fold skips them
 * silently; `diagram` frames render inline as a titled card (the dedicated
 * pane shows the picture itself).
 *
 * A message being regenerated in place (regeneratingMessageId) renders from
 * the live buffer, dimming its old content until new prose arrives.
 *
 * Round 3 (owner):
 *  - every finished answer carries a quiet footer row: a copy ICON that is
 *    always there (no hover reveal) beside the cost, the way ChatGPT and
 *    Claude place them;
 *  - an activity run ends with how long the lookups took, when this browser
 *    watched them happen;
 *  - a turn stored at several depths draws ONE answer, the one being read.
 *
 * Round 5 (owner, on a session that stacked 24 chips above the answer):
 *  - a FINISHED turn's tool work collapses into one summary row carrying the
 *    step count and the elapsed time (ActivityRun); a streaming turn keeps
 *    its chips, because during a 68-second wait they are the only news;
 *  - the elapsed time left the last chip and joined that summary row;
 *  - the footer names the depth a turn was written at ONLY when it differs
 *    from the level the depth control is sitting on — a badge that always
 *    said the same word as the control beside it would be furniture.
 */

import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import type { ChatMessage, ChatSessionSummary, Ulid } from '../../api/types';
import { toolElapsedMs, useChatStore, useSessionLive } from '../../stores/chatStore';
import { formatElapsedMs } from '../../lib/format';
import { frameData, frameType } from '../../lib/frames';
import { useNowTick } from '../liveinfra/useNowTick';
import { StreamErrorNote, TurnUsage } from './cards';
import { ActivityRun, type ActivityItem } from './chips';
import { copyTextToClipboard, turnCopyMarkdown } from './copyMarkdown';
import { MarkdownProse } from './markdown';
import { refreshMarkers } from './refreshMarkers';
import { buildTurnBlocks, type SessionDiagram, type TurnBlock } from './transcript';


/**
 * How close to the bottom (px) still counts as "riding the bottom" for the
 * stream's auto-follow. A line or two of slack: sub-pixel scroll positions
 * and the last line's own height must not read as "the user scrolled away".
 */
export const FOLLOW_SLACK_PX = 40;

/**
 * The while-answering mark (round 9, owner: "when a turn is answering, show
 * some animation or something to indicate it's thinking"): three dots
 * breathing in sequence, in the accent, where the static caret used to sit.
 * Reduced motion gets the still caret back (styles/index.css owns both).
 */
function ThinkingPulse() {
  return (
    <div data-testid="thinking-pulse" aria-hidden className="lm-thinking-pulse mt-1 text-(--accent)">
      <span />
      <span />
      <span />
    </div>
  );
}

function AgentTurn({ children }: { children: ReactNode }) {
  return (
    <div
      data-testid="agent-turn"
      // No `group` here any more: the only thing that used it was the cost
      // footer brightening on hover, and round 5 stopped it doing that.
      className="pl-4"
      style={{ borderLeft: '2px solid var(--accent)' }}
    >
      {children}
    </div>
  );
}

function UserTurn({ text }: { text: string }) {
  // Right-placed (ux round 2026-08-25 item 2): a subtle right-inset block,
  // the mirror of the agent's left accent rule, kept bubble-free in the
  // sans UI face.
  //
  // Placed right, read left (2026-09-08, owner on a two-line question whose
  // lines started three quarters of the column apart): the BLOCK sits on the
  // right, the TEXT inside it stays left-aligned, so every wrapped line
  // starts where the last one did. The width is capped so a long question
  // becomes a paragraph inset from the left rather than a full-width one
  // whose only trace of "right" was a ragged left edge.
  return (
    <div
      data-testid="user-turn"
      className="ml-auto w-fit max-w-[80%] pr-4 text-left"
      style={{ borderRight: '2px solid var(--border-strong)' }}
    >
      <div className="mb-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] font-medium tracking-[var(--text-micro-tracking)] uppercase text-tertiary">
        You
      </div>
      {/* Round 9 (owner: "why are my messages in the chat smaller font? It
          should be consistent"): the reader's words wear the same transcript
          type the guide's do. */}
      {/* The reader's words as typed: line breaks and runs of spaces kept
          (2026-09-09: a question written as a list arrived as one paragraph). */}
      <div
        data-testid="user-turn-text"
        className="font-medium whitespace-pre-wrap [overflow-wrap:anywhere] text-primary text-[length:var(--text-transcript-size)] leading-[var(--text-transcript-line)]"
      >
        {text}
      </div>
    </div>
  );
}

/**
 * A diagram in the transcript: a reference, and a QUIET one.
 *
 * The pane beside the conversation is where a diagram is legible; squeezing
 * the same picture into a 460px column produced a thumbnail nobody could read.
 * So this has always been a pointer rather than a drawing.
 *
 * Round 4 (owner, on one screen carrying the same long diagram title three
 * times): "Why is 'How one brand's daily visibility run turns questions into
 * dashboard scores' so highlighted? What value am I supposed to get from it?"
 * None, three times over. The title belongs to the PANE, which is where the
 * picture is; here it was a filled accent block shouting a sentence the reader
 * had already read twice. What is left is the one thing this control is for:
 * a small line saying there is a diagram, and where it is. The full title
 * rides along as the tooltip and the accessible name for anyone who wants it.
 *
 * The board is the conversation's ONE drawing, edited in place by every
 * answer (DECISIONS #96), so only the newest answer's card points at
 * something on screen: it says so, or brings the board back when the
 * infrastructure view is up. An earlier answer's drawing is on screen
 * nowhere any more, and its card is a label rather than a button that
 * pretends otherwise (2026-09-08).
 */
function DiagramCard({ data }: { data: SessionDiagram }) {
  // The board is the mounted session's newest drawing; this transcript is
  // the mounted session's.
  const isBoard = useChatStore(
    (s) =>
      s.activeSessionId !== null &&
      s.diagrams[s.activeSessionId]?.at(-1)?.diagram_id === data.diagram_id,
  );
  const paneView = useChatStore((s) => s.paneView);
  const setPaneView = useChatStore((s) => s.setPaneView);
  const showing = isBoard && paneView === 'board';
  const shell =
    'my-2 inline-flex max-w-full items-center gap-1.5 rounded-chip border bg-transparent px-2 py-1 text-left text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)]';

  if (!isBoard) {
    return (
      <span
        data-testid="diagram-card"
        data-diagram-id={data.diagram_id}
        data-diagram-kind={data.kind}
        data-board="earlier"
        title={data.title}
        aria-label={`Diagram: ${data.title}`}
        className={`${shell} border-default text-tertiary`}
      >
        <DiagramGlyph />
        <span className="font-medium">Diagram</span>
        <span aria-hidden>·</span>
        <span className="min-w-0 truncate">an earlier version of the board</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      data-testid="diagram-card"
      data-diagram-id={data.diagram_id}
      data-diagram-kind={data.kind}
      aria-current={showing ? 'true' : undefined}
      title={data.title}
      aria-label={`Diagram: ${data.title}`}
      onClick={() => setPaneView('board')}
      className={`${shell} cursor-pointer transition-colors duration-(--motion-hover) hover:text-primary ${
        showing ? 'border-strong text-secondary' : 'border-default text-tertiary hover:border-strong'
      }`}
    >
      <DiagramGlyph />
      <span className="font-medium">Diagram</span>
      <span aria-hidden>·</span>
      <span className="min-w-0 truncate">
        {showing ? 'on the board beside the chat' : 'show the board'}
      </span>
    </button>
  );
}

function DiagramGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      className="shrink-0"
      aria-hidden
    >
      <rect x="3" y="4" width="7" height="5" rx="1.5" />
      <rect x="14" y="15" width="7" height="5" rx="1.5" />
      <path d="M10 6.5h3a1.5 1.5 0 0 1 1.5 1.5v9.5" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4.5A1.5 1.5 0 0 1 3 13.5v-9A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5V5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m5 12.5 4.5 4.5L19 7" />
    </svg>
  );
}

/**
 * Per-turn copy (round 3, owner: "'Copy text' only appears on hover. It
 * should always appear as copy icon instead. Also why only copy text? It
 * should copy as markdown so any answer can be included.").
 *
 * So: an icon, always visible, and the clipboard gets markdown — prose plus
 * every diagram as a ```mermaid fence, which renders as the picture wherever
 * it is pasted. The tool activity stays behind; it is stage direction.
 */
function CopyTurnButton({ blocks }: { blocks: TurnBlock[] }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const copy = async () => {
    const ok = await copyTextToClipboard(turnCopyMarkdown(blocks));
    if (!ok) return;
    setCopied(true);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      data-testid="copy-turn"
      data-copied={copied ? 'true' : undefined}
      title={copied ? 'Copied as markdown' : 'Copy as markdown'}
      aria-label={copied ? 'Copied as markdown' : 'Copy as markdown'}
      onClick={() => void copy()}
      className={`flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-chip border border-transparent bg-transparent transition-colors duration-(--motion-hover) hover:border-default hover:text-primary ${
        copied ? 'text-(--accent)' : 'text-tertiary'
      }`}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
}

// Memoized for the streaming path: every delta re-renders the list, but a
// FINISHED turn's blocks array is the same reference each time (the `turns`
// useMemo), so the whole turn skips. Only the live turn re-renders per frame.
const TurnBlocks = memo(function TurnBlocks({
  blocks,
  elapsedMs = null,
  live = false,
  onRetry,
}: {
  blocks: TurnBlock[];
  /** tool time for this turn, shown once on the collapsed summary row */
  elapsedMs?: number | null;
  /** true while this turn is still streaming (chips stay open, no summary) */
  live?: boolean;
  /**
   * Set on the newest turn only, when its error says a retry stands a chance:
   * re-sends the question under it. A reader whose turn failed at the first
   * word (a rate limit, an outage) keeps the question AND gets a way back.
   */
  onRetry?: () => void;
}) {
  // ONE run per turn, drawn where the WORK began — the first tool call or
  // the first thought, whichever came first. A turn that thinks, writes a
  // sentence and then looks something else up still has one clock and one
  // step count, so it gets one door rather than a door per stretch. Thinking
  // usually opens a turn, which puts the run above the answer: the reader
  // watches the working, then reads what it produced.
  const items: ActivityItem[] = useMemo(
    () =>
      blocks.flatMap<ActivityItem>((block, i) => {
        if (block.kind === 'activity') {
          const { toolId, tool, summary } = block;
          return [{ kind: 'step', toolId, tool, summary }];
        }
        if (block.kind === 'thinking') {
          return [{ kind: 'thinking', key: `thinking-${i}`, text: block.text }];
        }
        return [];
      }),
    [blocks],
  );
  const firstWork = blocks.findIndex((b) => b.kind === 'activity' || b.kind === 'thinking');
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.kind) {
          case 'prose':
            return <MarkdownProse key={i} text={block.text} />;
          case 'activity':
          case 'thinking':
            return i === firstWork ? (
              <ActivityRun key={i} items={items} elapsedMs={elapsedMs} live={live} />
            ) : null;
          case 'diagram':
            return <DiagramCard key={block.data.diagram_id} data={block.data} />;
          case 'usage':
            // The cost sits in the turn footer beside Copy, not mid-column.
            return null;
          case 'error':
            return (
              <StreamErrorNote
                key={i}
                message={block.message}
                onRetry={block.retryable && onRetry !== undefined ? onRetry : undefined}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
});

/**
 * The code changed here (round 5, owner: "if I refresh mid session, does it
 * reflect on the newest one? How do we indicate that in the UI?").
 *
 * A thin line across the column with a short sentence in it, at the exact
 * point in the conversation where the analysis moved. Above it, the answers
 * describe the code as it was; below it, the newer code. It is a fact about
 * the reading order, not a warning, so it is drawn in the same quiet grey as
 * everything else that is furniture.
 */
function RefreshLine({ detail }: { detail: string | null }) {
  return (
    <div
      data-testid="refresh-marker"
      data-detail={detail ?? undefined}
      title="Answers above this line describe the code as it was then. Anything you ask from here on uses the newest code."
      className="flex items-center gap-2 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
    >
      <span aria-hidden className="h-px flex-1 bg-(--border-default)" />
      <span className="shrink-0">
        The code changed here
        {detail !== null ? ` · ${detail}` : ''}
      </span>
      <span aria-hidden className="h-px flex-1 bg-(--border-default)" />
    </div>
  );
}

/**
 * The pulse with a clock beside it while the answer is still coming (owner,
 * 2026-09-05: "When answer is generating, it needs to show runtime as well.
 * Right now we only show time elapsed only when the turn ends"). Ticks once
 * a second from the moment the stream opened; the footer's number, stamped
 * by the server, takes over when the turn lands.
 */
function LiveClock({ startedAt }: { startedAt: number | null }) {
  const now = useNowTick(1000);
  return (
    <div className="flex items-center gap-2">
      <ThinkingPulse />
      {startedAt !== null ? (
        <span
          data-testid="live-elapsed"
          title="How long this answer has been running"
          className="text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary tabular-nums"
        >
          {formatElapsedMs(Math.max(0, now - startedAt))}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The quiet row under a finished answer: copy on the left, what the turn
 * cost beside it. One row, always present, nothing hidden behind hover.
 */
function TurnFooter({
  blocks,
  model,
  showCopy,
  durationMs = null,
}: {
  blocks: TurnBlock[];
  model?: string | null;
  showCopy: boolean;
  /** wall clock for the whole turn (round 9 #80c); null on pre-fix rows */
  durationMs?: number | null;
}) {
  const usage = blocks.find(
    (b): b is Extract<TurnBlock, { kind: 'usage' }> => b.kind === 'usage',
  );
  if (!showCopy && usage === undefined && durationMs === null) return null;
  return (
    <div data-testid="turn-footer" className="mt-1.5 flex items-center gap-2">
      {showCopy ? <CopyTurnButton blocks={blocks} /> : null}
      {usage !== undefined ? <TurnUsage usage={usage.usage} model={model} /> : null}
      {durationMs !== null && durationMs > 0 ? (
        // Round 9 (owner: "I would like to see time elapsed at the end of
        // each turn"): the wall clock the reader watched live, kept across
        // reloads because the server stamps it at persist.
        <span
          data-testid="turn-duration"
          title="How long this answer took"
          className="text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
        >
          {formatElapsedMs(durationMs)}
        </span>
      ) : null}
    </div>
  );
}

export function MessageList({
  sessionId,
  messages,
  session,
  onRetry,
}: {
  /** the session this transcript renders — keys its live stream buffer */
  sessionId: Ulid | null;
  messages: ChatMessage[];
  /**
   * Re-sends a question. Offered on the newest turn when it ended in a
   * retryable error (2026-09-10: a failed turn used to take the question with
   * it; now the question stays, the error is its answer, and this is the way
   * back). Absent = no control.
   */
  onRetry?: (text: string) => void;
  /**
   * When provided, completed agent turns grow the per-turn copy control. The
   * transcript carries no other controls: the Simpler/Shorter/More
   * detail/Show the code handles are gone, and so is the depth dial
   * (DECISIONS #95).
   */
  session?: ChatSessionSummary;
}) {
  const live = useSessionLive(sessionId);
  const {
    frames: streamFrames,
    streamingMessageId,
    streaming,
    error: streamError,
    pendingUserText,
    turnStartedAt,
  } = live;

  const containerRef = useRef<HTMLDivElement>(null);
  /**
   * Whether the reader was AT the bottom before the newest content landed.
   * Written by the scroll handler (which fires for our own programmatic
   * jumps too, keeping it true while following), read by the follow effect:
   * scrolling up mid-stream flips it false and the stream stops fighting
   * the reader for the scrollbar. Starts true so a freshly opened session
   * lands on its newest turn.
   */
  const followRef = useRef(true);

  // The live buffer renders only until the session refetch delivers the turn.
  const liveTurnPersisted =
    streamingMessageId !== null && messages.some((m) => m.id === streamingMessageId);
  const showLiveTurn = streamFrames.length > 0 && !liveTurnPersisted;
  // The same goes for the live buffer's error note: a Stop (or a failure) is
  // persisted by the server as the row's own error frame, with the same
  // words, so once that row has landed the note is already on screen inside
  // the turn. Until then, or when nothing was persisted (a refused send, a
  // POST that failed), the note stands alone. Checked by id when the turn
  // had started, by the frame's words when it was cut before message.start.
  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  const liveErrorPersisted =
    streamError !== null &&
    (liveTurnPersisted ||
      (lastAssistant?.frames ?? []).some(
        (f) => frameType(f) === 'error' && (frameData(f) as { message?: string }).message === streamError,
      ));
  const showLiveError = streamError !== null && !liveErrorPersisted;

  // The optimistic user echo renders only until its persisted row lands: a
  // session fetch resolving mid-stream (e.g. the session view mounting after
  // send) already contains the user message — showing both duplicates it.
  const lastMessage = messages[messages.length - 1];
  const pendingUserPersisted =
    lastMessage !== undefined &&
    lastMessage.role === 'user' &&
    (lastMessage.text ?? '') === pendingUserText;
  const showPendingUser = pendingUserText !== null && !pendingUserPersisted;

  // One turn, one answer: each row's own body (DECISIONS #95).
  const turns = useMemo(
    () =>
      messages.map((message) => {
        if (message.role !== 'assistant') {
          return { message, blocks: null, model: message.model };
        }
        const blocks = message.frames !== null ? buildTurnBlocks(message.frames) : null;
        return { message, blocks, model: message.model };
      }),
    [messages],
  );

  const liveBlocks = useMemo(
    () => (streamFrames.length > 0 ? buildTurnBlocks(streamFrames) : []),
    [streamFrames],
  );

  // The one turn that may carry a Retry: the newest, when it ended in a
  // retryable error, while nothing streams, and the row above it is the
  // question to send again.
  const retryText = useMemo(() => {
    if (onRetry === undefined || streaming || turns.length < 2) return null;
    const last = turns[turns.length - 1];
    const before = turns[turns.length - 2];
    if (last === undefined || before === undefined) return null;
    if (last.message.role !== 'assistant' || before.message.role !== 'user') return null;
    const ended = last.blocks?.find((b) => b.kind === 'error');
    if (ended === undefined || !ended.retryable) return null;
    const text = before.message.text ?? '';
    return text.length > 0 ? text : null;
  }, [onRetry, streaming, turns]);
  const retryLast = useCallback(() => {
    if (onRetry !== undefined && retryText !== null) onRetry(retryText);
  }, [onRetry, retryText]);

  // Where the analysis moved mid-conversation. Rides the session read and
  // the session list the app already fetches: no request of its own.
  const markersByIndex = useMemo(() => {
    const byIndex = new Map<number, string | null>();
    for (const marker of refreshMarkers(messages, session?.staleness)) {
      byIndex.set(marker.index, marker.detail);
    }
    return byIndex;
  }, [messages, session?.staleness]);
  const trailingMarker = markersByIndex.has(messages.length)
    ? (markersByIndex.get(messages.length) ?? null)
    : undefined;

  // Tool time, measured here as the frames landed (persisted history carries
  // no timestamps, so a replayed turn shows nothing).
  const elapsedByMessage = useChatStore((s) => s.turnElapsedMs);
  const liveElapsed = toolElapsedMs(live);

  // Follow the newest content as it arrives — but only while the reader is
  // riding the bottom. The handler below records where they were BEFORE this
  // update grew the scrollHeight; jumping them back down after they scrolled
  // up to re-read something is the classic streaming-chat fight, and the
  // stream loses it on purpose. Reaching the bottom again re-arms the follow.
  useEffect(() => {
    const el = containerRef.current;
    if (el === null || !followRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, streamFrames]);

  return (
    <div
      ref={containerRef}
      data-testid="message-list"
      onScroll={() => {
        const el = containerRef.current;
        if (el === null) return;
        followRef.current =
          el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_SLACK_PX;
      }}
      className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-6"
    >
      {turns.map(({ message, blocks, model }, index) => (
        <Fragment key={message.id}>
          {markersByIndex.has(index) ? (
            <RefreshLine detail={markersByIndex.get(index) ?? null} />
          ) : null}
          {message.role === 'user' ? (
            <UserTurn text={message.text ?? ''} />
          ) : (
            <AgentTurn>
              <TurnBlocks
                blocks={blocks ?? []}
                elapsedMs={elapsedByMessage[message.id] ?? null}
                onRetry={retryText !== null && index === turns.length - 1 ? retryLast : undefined}
              />
              <TurnFooter
                blocks={blocks ?? []}
                model={model}
                showCopy={session !== undefined}
                durationMs={message.duration_ms ?? null}
              />
            </AgentTurn>
          )}
        </Fragment>
      ))}
      {/* The last answer describes older code than the next one will: the
          line sits under everything already read, not over anything. */}
      {trailingMarker !== undefined ? <RefreshLine detail={trailingMarker} /> : null}
      {showPendingUser && pendingUserText !== null ? <UserTurn text={pendingUserText} /> : null}
      {showLiveTurn ? (
        <AgentTurn>
          <TurnBlocks blocks={liveBlocks} elapsedMs={liveElapsed} live={streaming} />
          {streaming ? (
            <LiveClock startedAt={turnStartedAt} />
          ) : (
            // The turn just completed and the refetch hasn't replaced the
            // live buffer yet: copy is offered the moment the turn is done.
            <TurnFooter blocks={liveBlocks} showCopy={session !== undefined} />
          )}
        </AgentTurn>
      ) : null}
      {showLiveError && streamError !== null ? <StreamErrorNote message={streamError} /> : null}
    </div>
  );
}
